import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { VALIDATION_MESSAGES } from '../constants/messages';

/**
 * Page shown when user clicks the unsubscribe link in an email.
 * Calls the unsubscribe edge function with the token and displays success or error.
 */
const UnsubscribePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage(VALIDATION_MESSAGES.UNSUBSCRIBE_PAGE.MISSING_LINK);
      return;
    }

    let cancelled = false;
    setStatus('loading');

    const supabaseUrl = (import.meta as any).env?.VITE_SUPABASE_URL || '';
    const anonKey = (import.meta as any).env?.VITE_SUPABASE_ANON_KEY || '';
    const url = `${supabaseUrl}/functions/v1/unsubscribe?token=${encodeURIComponent(token)}`;

    fetch(url, {
      method: 'GET',
      headers: {
        ...(anonKey ? { Authorization: `Bearer ${anonKey}` } : {}),
        Accept: 'application/json',
      },
    })
      .then(async (res) => {
        if (cancelled) return;
        if (res.ok) {
          setStatus('success');
        } else {
          setStatus('error');
          try {
            const body = await res.json();
            setMessage(body?.error || VALIDATION_MESSAGES.UNSUBSCRIBE_PAGE.LINK_INVALID_OR_USED);
          } catch {
            setMessage(VALIDATION_MESSAGES.UNSUBSCRIBE_PAGE.LINK_INVALID_OR_USED);
          }
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus('error');
          setMessage(VALIDATION_MESSAGES.UNSUBSCRIBE_PAGE.SOMETHING_WRONG);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-light-gray to-sky-blue/20 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center"
      >
        {status === 'loading' && (
          <>
            <Loader2 className="w-16 h-16 text-electric-blue animate-spin mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-800">Unsubscribing…</h1>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-800 mb-2">You're unsubscribed</h1>
            <p className="text-gray-600 mb-6">
              You will no longer receive marketing or introduction emails from Pharma Connect
              Navigator.
            </p>
            <Link
              to="/"
              className="inline-block bg-electric-blue text-white px-6 py-3 rounded-lg font-medium hover:opacity-90"
            >
              Return to home
            </Link>
          </>
        )}

        {status === 'error' && (
          <>
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-800 mb-2">Unable to unsubscribe</h1>
            <p className="text-gray-600 mb-6">{message}</p>
            <Link
              to="/"
              className="inline-block bg-electric-blue text-white px-6 py-3 rounded-lg font-medium hover:opacity-90"
            >
              Return to home
            </Link>
          </>
        )}

        {status === 'idle' && token === null && (
          <>
            <XCircle className="w-16 h-16 text-amber-500 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-gray-800 mb-2">No unsubscribe link</h1>
            <p className="text-gray-600 mb-6">
              Please use the unsubscribe link from one of our emails.
            </p>
            <Link
              to="/"
              className="inline-block bg-electric-blue text-white px-6 py-3 rounded-lg font-medium hover:opacity-90"
            >
              Return to home
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default UnsubscribePage;
